import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { getSupabaseAdmin } from "@/integrations/external-supabase/client.server";
import { resolveCurrentUserId } from "@/lib/articulos.server";
import { getScope } from "@/lib/solicitudes-scope.server";
import { upsertContactoByIdentity, resolveContactoIdDeUsuario } from "@/lib/contactos-upsert.server";
import { applyAsignadoFilter } from "@/lib/scope-filtro";


const Id = z.object({ id: z.string().uuid() });

const SolicitudEstado = z.enum(["sin_atender", "atendida", "no_contesta", "descartada", "cancelada"]);

const SolicitudInput = z.object({
  contacto_id: z.string().uuid(),
  anuncio_id: z.string().uuid().nullable().optional(),
  agencia_id: z.string().uuid().nullable().optional(),
  asignado_a: z.string().uuid().nullable().optional(),
  estado: SolicitudEstado.default("sin_atender"),
  solicitud: z
    .string()
    .trim()
    .max(4000)
    .nullable()
    .optional()
    .or(z.literal("").transform(() => null)),
  notas_internas: z
    .string()
    .trim()
    .max(4000)
    .nullable()
    .optional()
    .or(z.literal("").transform(() => null)),
});

export const SOLICITUD_ESTADOS = SolicitudEstado.options;
export const SOLICITUD_ESTADO_LABEL: Record<
  z.infer<typeof SolicitudEstado>,
  string
> = {
  sin_atender: "Sin atender",
  atendida: "Atendida",
  no_contesta: "No contesta",
  descartada: "Descartada",
  cancelada: "Cancelada",
};


const ListScope = z
  .object({
    agenciaVista: z.string().nullable().optional(),
    asignado: z.string().nullable().optional(),
  })
  .default({});

// ───────── Lista ─────────
export const listSolicitudes = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => ListScope.parse(d ?? {}))
  .handler(async ({ data: input }) => {
  const sb = getSupabaseAdmin();
  const scope = await getScope();
  const { clampAsignado } = await import("@/lib/scope-permisos.server");
  const asignadoFiltro = await clampAsignado(input.asignado);


  let anuncioFilter: string | null = null;
  let agenciaFilter: string | null = null;
  // Agencias a las que limitar el listado (miembro: las suyas; staff: la impersonada)
  const scopeAgenciaIds = scope.isStaff
    ? input.agenciaVista && input.agenciaVista !== "sin_agencia"
      ? [input.agenciaVista]
      : null
    : scope.agenciaIds;

  if (scopeAgenciaIds) {
    if (scopeAgenciaIds.length === 0) return [];
    const { data: anuncios } = await sb
      .from("anuncios")
      .select("id")
      .in("agencia_id", scopeAgenciaIds);
    const allowedAnuncioIds = (anuncios ?? []).map((a: { id: string }) => a.id);
    anuncioFilter = allowedAnuncioIds.length
      ? `anuncio_id.in.(${allowedAnuncioIds.join(",")})`
      : null;
    agenciaFilter = `agencia_id.in.(${scopeAgenciaIds.join(",")})`;
  }

  const { fetchAllPaged } = await import("@/lib/pagination.server");
  const rawData = await fetchAllPaged<any>((from, to) => {
    let q = sb
      .from("solicitudes")
      .select(
        "id, contacto_id, anuncio_id, agencia_id, demanda_id, asignado_a, estado, origen, solicitud, notas_internas, notificacion, created_at, updated_at, " +
          "contacto:contactos(id, nombre, apellidos, email, telefono), " +
          "anuncio:anuncios(id, referencia, alias, agencia_id, provincia_id, visibilidad, anuncio_contactos(rol, contacto:contactos(id, nombre, apellidos, email, telefono))), " +
          "agencia:agencias(id, nombre, estado), " +
          "demanda:demandas(id, referencia, titulo)",
      )
      .order("created_at", { ascending: false })
      .range(from, to);
    if (scopeAgenciaIds) {
      q = q.or([anuncioFilter, agenciaFilter].filter(Boolean).join(","));
    } else if (scope.isStaff && input.agenciaVista === "sin_agencia") {
      q = q.is("agencia_id", null);
    }
    q = applyAsignadoFilter(q, asignadoFiltro);

    return q;
  });


  // Clientes cedidos: la RLS de `contactos` deja el embed vacío si el cliente
  // no es de la cartera de la agencia; se completa desde la vista.
  const { rellenarContactos } = await import("@/lib/clientes-cedidos.server");
  const data = await rellenarContactos((rawData ?? []) as Record<string, unknown>[]) as any[];

  // Fallback: si no viene la agencia directa, resuélvela desde el anuncio
  const missingAgenciaIds = Array.from(
    new Set(
      (data ?? [])
        .filter((s: any) => !s.agencia && s.anuncio?.agencia_id)
        .map((s: any) => s.anuncio.agencia_id as string),
    ),
  );
  let agenciasMap: Record<string, { nombre: string; estado: string | null }> = {};
  if (missingAgenciaIds.length > 0) {
    const { data: agenciasData } = await sb
      .from("agencias")
      .select("id, nombre, estado")
      .in("id", missingAgenciaIds);
    agenciasMap = Object.fromEntries(
      (agenciasData ?? []).map((a: any) => [a.id, { nombre: a.nombre, estado: a.estado ?? null }]),
    );
  }

  return (data ?? []).map((s: any) => {
    const { anuncio_contactos, ...anuncioRest } = s.anuncio ?? {};
    const anunciante = (anuncio_contactos ?? []).find(
      (ac: any) => ac.rol === "anunciante",
    )?.contacto ?? null;
    return {
      ...s,
      anunciante,
      anuncio: s.anuncio
        ? {
            ...anuncioRest,
            agencia: s.anuncio.agencia_id
              ? {
                  nombre: agenciasMap[s.anuncio.agencia_id]?.nombre ?? s.agencia?.nombre ?? null,
                  estado: agenciasMap[s.anuncio.agencia_id]?.estado ?? s.agencia?.estado ?? null,
                }
              : null,
          }
        : null,
    };
  });
});

// ───────── Resumen de una solicitud (panel lateral) ─────────
export const getSolicitudResumen = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => Id.parse(d))
  .handler(async ({ data }) => {
    const sb = getSupabaseAdmin();
    const scope = await getScope();
    const { data: s0, error } = await sb
      .from("solicitudes")
      .select(
        "id, contacto_id, anuncio_id, agencia_id, demanda_id, asignado_a, estado, solicitud, notas_internas, created_at, " +
          "contacto:contactos(id, nombre, apellidos, email, telefono), " +
          "anuncio:anuncios(id, referencia, alias, agencia_id), " +
          "agencia:agencias(id, nombre), " +
          "demanda:demandas(id, referencia, titulo)",
      )
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!s0) return null;
    const { rellenarContactos } = await import("@/lib/clientes-cedidos.server");
    const [s] = await rellenarContactos([s0 as unknown as Record<string, unknown>]);
    const row = s as unknown as {
      agencia_id: string | null;
      anuncio: { agencia_id: string | null } | null;
    };
    if (!scope.isStaff) {
      const agencias = [row.agencia_id, row.anuncio?.agencia_id].filter(Boolean) as string[];
      if (!agencias.some((a) => scope.agenciaIds.includes(a))) throw new Error("No autorizado");
    }
    return s as unknown as {
      id: string;
      estado: string;
      solicitud: string | null;
      notas_internas: string | null;
      created_at: string;
      contacto: { id: string; nombre: string | null; apellidos: string | null; email: string | null; telefono: string | null } | null;
      anuncio: { id: string; referencia: string | null; alias: string | null } | null;
      agencia: { id: string; nombre: string } | null;
      demanda: { id: string; referencia: string | null; titulo: string | null } | null;
    };
  });

// ───────── Lista por contacto ─────────
export const listSolicitudesByAnuncio = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) =>
    z.object({ anuncio_id: z.string().uuid() }).parse(d),
  )
  .handler(async ({ data }) => {
    const sb = getSupabaseAdmin();
    const scope = await getScope();
    if (!scope.isStaff) {
      if (scope.agenciaIds.length === 0) return [];
      const { data: a } = await sb
        .from("anuncios")
        .select("agencia_id")
        .eq("id", data.anuncio_id)
        .maybeSingle();
      if (!a || !a.agencia_id || !scope.agenciaIds.includes(a.agencia_id))
        return [];
    }
    const { data: rows, error } = await sb
      .from("solicitudes")
      .select(
        "id, contacto_id, anuncio_id, asignado_a, estado, solicitud, notas_internas, created_at, updated_at, " +
          "contacto:contactos(id, nombre, apellidos, email, telefono)",
      )
      .eq("anuncio_id", data.anuncio_id)
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    const { rellenarContactos } = await import("@/lib/clientes-cedidos.server");
    return rellenarContactos(
      (rows ?? []) as unknown as Record<string, unknown>[],
    ) as unknown as Promise<NonNullable<typeof rows>>;
  });

export const listSolicitudesByContacto = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) =>
    z.object({ contacto_id: z.string().uuid() }).parse(d),
  )
  .handler(async ({ data }) => {
    const sb = getSupabaseAdmin();
    const scope = await getScope();
    let q = sb
      .from("solicitudes")
      .select(
        "id, contacto_id, anuncio_id, asignado_a, estado, solicitud, notas_internas, created_at, updated_at, " +
          "anuncio:anuncios(id, referencia, alias, agencia_id)",
      )
      .eq("contacto_id", data.contacto_id)
      .order("created_at", { ascending: false });

    if (!scope.isStaff) {
      if (scope.agenciaIds.length === 0) return [];
      const { data: anuncios } = await sb
        .from("anuncios")
        .select("id")
        .in("agencia_id", scope.agenciaIds);
      const allowedIds = (anuncios ?? []).map((a: { id: string }) => a.id);
      // Las de sus anuncios y las asignadas a su agencia (aunque el anuncio sea de otra).
      q = q.or(
        [
          allowedIds.length ? `anuncio_id.in.(${allowedIds.join(",")})` : null,
          `agencia_id.in.(${scope.agenciaIds.join(",")})`,
        ]
          .filter(Boolean)
          .join(","),
      );
    }

    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    return rows ?? [];
  });

export const listSolicitudesByAgencia = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) =>
    z.object({ agencia_id: z.string().uuid() }).parse(d),
  )
  .handler(async ({ data }) => {
    const sb = getSupabaseAdmin();
    const scope = await getScope();
    if (!scope.isStaff) {
      if (!scope.agenciaIds.includes(data.agencia_id)) return [];
    }
    const { data: anuncios } = await sb
      .from("anuncios")
      .select("id")
      .eq("agencia_id", data.agencia_id);
    const ids = (anuncios ?? []).map((a: { id: string }) => a.id);
    if (ids.length === 0) return [];
    const { data: rows, error } = await sb
      .from("solicitudes")
      .select(
        "id, contacto_id, anuncio_id, asignado_a, estado, solicitud, notas_internas, created_at, updated_at, " +
          "contacto:contactos(id, nombre, apellidos, email, telefono), " +
          "anuncio:anuncios(id, referencia, alias, agencia_id)",
      )
      .in("anuncio_id", ids)
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    const { rellenarContactos } = await import("@/lib/clientes-cedidos.server");
    return rellenarContactos(
      (rows ?? []) as unknown as Record<string, unknown>[],
    ) as unknown as Promise<NonNullable<typeof rows>>;
  });

// ───────── Crear ─────────
export const createSolicitud = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => SolicitudInput.parse(d))
  .handler(async ({ data }) => {
    const sb = getSupabaseAdmin();
    const userId = await resolveCurrentUserId();
    // Autoasignación al creador (staff o miembro editor+ de la agencia).
    const { resolveAsignadoInicial } = await import("@/lib/asignacion.server");
    const asignadoA = await resolveAsignadoInicial(
      userId,
      data.agencia_id ?? null,
      data.asignado_a ?? null,
    );
    const { data: row, error } = await sb
      .from("solicitudes")
      .insert({
        contacto_id: data.contacto_id,
        anuncio_id: data.anuncio_id ?? null,
        agencia_id: data.agencia_id ?? null,
        asignado_a: asignadoA,
        created_by: userId,
        origen: "manual",
        estado: data.estado,
        solicitud: data.solicitud ?? null,
        notas_internas: data.notas_internas ?? null,
      })
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    const { prepararNotificacionSolicitud } = await import("./solicitud-email.server");
    await prepararNotificacionSolicitud(sb, row.id);
    return { ok: true, id: row.id };
  });

/** Staff, o miembro de la agencia de la solicitud (y de la agencia destino si cambia). */
async function assertSolicitudEditable(id: string, agenciaDestino?: string | null) {
  const scope = await getScope();
  if (scope.isStaff) return scope;
  const { data: row } = await getSupabaseAdmin()
    .from("solicitudes")
    .select("agencia_id, anuncio:anuncios(agencia_id)")
    .eq("id", id)
    .maybeSingle();
  const ag = (row as any)?.agencia_id ?? (row as any)?.anuncio?.agencia_id ?? null;
  if (!ag || !scope.agenciaIds.includes(ag)) throw new Error("Forbidden");
  if (agenciaDestino && agenciaDestino !== ag && !scope.agenciaIds.includes(agenciaDestino))
    throw new Error("Forbidden");
  return scope;
}

// ───────── Actualizar ─────────
export const updateSolicitud = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => SolicitudInput.extend(Id.shape).parse(d))
  .handler(async ({ data }) => {
    // Solo staff o miembros de la agencia de la solicitud (no el usuario final).
    await assertSolicitudEditable(data.id, data.agencia_id ?? null);
    const sb = getSupabaseAdmin();
    const { id, ...rest } = data;
    // Estado previo del asignado (la BD puede autoasignar al actualizar)
    const { data: before } = await sb
      .from("solicitudes")
      .select("asignado_a")
      .eq("id", id)
      .maybeSingle();
    const { data: row, error } = await sb
      .from("solicitudes")
      .update({
        contacto_id: rest.contacto_id,
        anuncio_id: rest.anuncio_id ?? null,
        agencia_id: rest.agencia_id ?? null,
        // Solo tocamos asignado_a si viene explícito; si no, deja actuar a la BD.
        ...(rest.asignado_a !== undefined ? { asignado_a: rest.asignado_a } : {}),
        estado: rest.estado,
        solicitud: rest.solicitud ?? null,
        notas_internas: rest.notas_internas ?? null,
      })
      .eq("id", id)
      .select("asignado_a")
      .maybeSingle();
    if (error) throw new Error(error.message);
    return {
      ok: true,
      asignado_a: row?.asignado_a ?? null,
      asignado_a_prev: before?.asignado_a ?? null,
    };
  });

// ───────── Reasignar ─────────
export const assignSolicitud = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z
      .object({ id: z.string().uuid(), asignado_a: z.string().uuid().nullable() })
      .parse(d),
  )
  .handler(async ({ data }) => {
    const scope = await getScope();
    const sb = getSupabaseAdmin();
    if (!scope.isStaff) {
      const { data: row } = await sb
        .from("solicitudes")
        .select("agencia_id, anuncio:anuncios(agencia_id)")
        .eq("id", data.id)
        .maybeSingle();
      const ag = (row as any)?.agencia_id ?? (row as any)?.anuncio?.agencia_id ?? null;
      if (!ag || !scope.agenciaIds.includes(ag)) throw new Error("Forbidden");
    }
    const { error } = await sb
      .from("solicitudes")
      .update({ asignado_a: data.asignado_a })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ───────── Eliminar ─────────
export const deleteSolicitud = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => Id.parse(d))
  .handler(async ({ data }) => {
    await assertSolicitudEditable(data.id);
    const sb = getSupabaseAdmin();
    const { error } = await sb.from("solicitudes").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ───────── Preparar / re-preparar notificación (manual, staff-only) ─────────
export const prepararNotificacionManual = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => Id.parse(d))
  .handler(async ({ data }) => {
    const scope = await getScope();
    if (!scope.isStaff) throw new Error("Forbidden");
    const sb = getSupabaseAdmin();
    const { prepararNotificacionSolicitud } = await import("./solicitud-email.server");
    await prepararNotificacionSolicitud(sb, data.id);
    const { data: row } = await sb
      .from("solicitudes")
      .select("notificacion")
      .eq("id", data.id)
      .maybeSingle();
    return { ok: true, notificacion: row?.notificacion ?? null };
  });

// ───────── Crear solicitud pública (formulario en página del anuncio) ─────────
const SolicitudPublicaInput = z.object({
  anuncio_id: z.string().uuid(),
  nombre: z.string().trim().min(1).max(120),
  apellidos: z.string().trim().max(120).optional().or(z.literal("").transform(() => undefined)),
  email: z.string().trim().email().max(200),
  telefono: z.string().trim().max(40).optional().or(z.literal("").transform(() => undefined)),
  mensaje: z.string().trim().min(1).max(4000),
  ciudad: z.string().trim().max(120).optional().or(z.literal("").transform(() => undefined)),
  utm_source: z.string().trim().max(200).optional(),
  utm_medium: z.string().trim().max(200).optional(),
  utm_campaign: z.string().trim().max(200).optional(),
  utm_term: z.string().trim().max(200).optional(),
  utm_content: z.string().trim().max(200).optional(),
  utm_referrer: z.string().trim().max(500).optional(),
  utm_landing_page: z.string().trim().max(500).optional(),
});

export const createSolicitudPublica = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => SolicitudPublicaInput.parse(d))
  .handler(async ({ data }) => {
    const sb = getSupabaseAdmin();
    const userId = await resolveCurrentUserId();
    const { crearSolicitudPublica } = await import("./solicitudes-publicas.server");
    await crearSolicitudPublica(sb, data, userId);



    return { ok: true };
  });


type SolicitudPortalResult = {
  ok: true;
  existing: boolean;
  id: string;
  notificacion: Awaited<ReturnType<typeof import("./solicitud-email.server").prepararNotificacionSolicitud>> | null;
  demanda: { id: string; referencia: string | null } | null;
};

/**
 * Alta común de una solicitud de un usuario con cuenta sobre un anuncio
 * público: evita duplicados (misma persona y anuncio), la crea con su origen y
 * prepara el aviso al anunciante.
 */
async function insertarSolicitudPortal(
  sb: ReturnType<typeof getSupabaseAdmin>,
  input: {
    contactoId: string;
    anuncioId: string;
    demanda: { id: string; referencia: string | null; light: boolean } | null;
  },
): Promise<SolicitudPortalResult> {
  const { data: anuncio } = await sb
    .from("anuncios")
    .select("id, estado, visibilidad, agencia_id")
    .eq("id", input.anuncioId)
    .maybeSingle();
  if (!anuncio || anuncio.estado !== "publicado" || anuncio.visibilidad !== "publico") {
    throw new Error("Anuncio no disponible");
  }
  const demanda = input.demanda ? { id: input.demanda.id, referencia: input.demanda.referencia } : null;

  const { data: existing } = await sb
    .from("solicitudes")
    .select("id")
    .eq("contacto_id", input.contactoId)
    .eq("anuncio_id", input.anuncioId)
    .neq("estado", "cancelada")
    .limit(1)
    .maybeSingle();
  if (existing) return { ok: true, existing: true, id: existing.id as string, notificacion: null, demanda };

  const { data: inserted, error: eS } = await sb
    .from("solicitudes")
    .insert({
      contacto_id: input.contactoId,
      anuncio_id: input.anuncioId,
      agencia_id: anuncio.agencia_id ?? null,
      demanda_id: input.demanda?.id ?? null,
      origen: input.demanda ? (input.demanda.light ? "conector_light" : "conector") : "formulario_anuncio",
      estado: "sin_atender",
      solicitud: input.demanda
        ? "Solicitud creada desde el conector para más información sobre este local."
        : "Solicitud de más información sobre este local desde el portal.",
    })
    .select("id")
    .single();
  if (eS) throw new Error(eS.message);

  const { prepararNotificacionSolicitud } = await import("./solicitud-email.server");
  const notificacion = await prepararNotificacionSolicitud(sb, inserted.id);
  return { ok: true, existing: false, id: inserted.id as string, notificacion, demanda };
}

async function contactoDelUsuario(sb: ReturnType<typeof getSupabaseAdmin>, userId: string) {
  const { data: c } = await sb.from("contactos").select("id").eq("user_id", userId).maybeSingle();
  if (!c) throw new Error("Contacto no encontrado");
  return c.id as string;
}

// ───────── Crear solicitud desde el conector (demanda del usuario logueado) ─────────
export const createSolicitudDesdeDemanda = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z.object({
      demanda_id: z.string().uuid(),
      anuncio_id: z.string().uuid(),
    }).parse(d),
  )
  .handler(async ({ data }) => {
    const userId = await resolveCurrentUserId();
    if (!userId) throw new Error("Debes iniciar sesión");
    const sb = getSupabaseAdmin();
    const contactoId = await contactoDelUsuario(sb, userId);

    // Verificar que la demanda pertenece al contacto
    const { data: dem } = await sb
      .from("demandas")
      .select("id, referencia, metadata")
      .eq("id", data.demanda_id)
      .eq("contacto_id", contactoId)
      .maybeSingle();
    if (!dem) throw new Error("Demanda no encontrada");
    return insertarSolicitudPortal(sb, {
      contactoId,
      anuncioId: data.anuncio_id,
      demanda: {
        id: dem.id as string,
        referencia: (dem.referencia as string | null) ?? null,
        light: (dem.metadata as { light?: unknown } | null)?.light === true,
      },
    });
  });

// ───────── Crear solicitud desde la ficha del anuncio (portal) ─────────
/**
 * Pide información de un anuncio. Si viene de una demanda suya (?demanda=), la
 * usa; si no, usa la demanda activa que incluya este anuncio entre sus
 * coincidencias; y si ninguna encaja, la solicitud se crea sin demanda.
 */
export const createSolicitudDesdeAnuncio = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z.object({
      anuncio_id: z.string().uuid(),
      demanda_id: z.string().uuid().nullable().optional(),
    }).parse(d),
  )
  .handler(async ({ data }) => {
    const userId = await resolveCurrentUserId();
    if (!userId) throw new Error("Debes iniciar sesión");
    const sb = getSupabaseAdmin();
    const contactoId = await contactoDelUsuario(sb, userId);

    type Dem = { id: string; referencia: string | null; metadata: unknown };
    let elegida: Dem | null = null;
    if (data.demanda_id) {
      const { data: dem } = await sb
        .from("demandas")
        .select("id, referencia, metadata")
        .eq("id", data.demanda_id)
        .eq("contacto_id", contactoId)
        .maybeSingle();
      elegida = (dem as Dem | null) ?? null;
    }
    if (!elegida) {
      const { data: activas } = await sb
        .from("demandas")
        .select("id, referencia, metadata")
        .eq("contacto_id", contactoId)
        .eq("activa", true)
        .not("visibilidades", "cs", "{off_market}")
        .not("estado", "in", "(cancelada,archivada,completada)")
        .order("updated_at", { ascending: false });
      const ids = ((activas ?? []) as Dem[]).map((d) => d.id);
      if (ids.length) {
        const { data: matches } = await sb
          .from("demanda_matches_cache")
          .select("demanda_id")
          .eq("anuncio_id", data.anuncio_id)
          .in("demanda_id", ids);
        const conMatch = new Set((matches ?? []).map((m: { demanda_id: string }) => m.demanda_id));
        elegida = ((activas ?? []) as Dem[]).find((d) => conMatch.has(d.id)) ?? null;
      }
    }
    return insertarSolicitudPortal(sb, {
      contactoId,
      anuncioId: data.anuncio_id,
      demanda: elegida
        ? {
            id: elegida.id,
            referencia: elegida.referencia,
            light: (elegida.metadata as { light?: unknown } | null)?.light === true,
          }
        : null,
    });
  });

// ───────── Actualizar mensaje de una solicitud creada desde el conector ─────────
export const updateSolicitudMensajeConector = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z.object({
      solicitud_id: z.string().uuid(),
      mensaje: z.string().trim().min(1).max(4000),
    }).parse(d),
  )
  .handler(async ({ data }) => {
    const userId = await resolveCurrentUserId();
    if (!userId) throw new Error("Debes iniciar sesión");
    const sb = getSupabaseAdmin();
    const { data: c } = await sb
      .from("contactos")
      .select("id")
      .eq("user_id", userId)
      .maybeSingle();
    if (!c) throw new Error("Contacto no encontrado");
    const { error } = await sb
      .from("solicitudes")
      .update({ solicitud: data.mensaje })
      .eq("id", data.solicitud_id)
      .eq("contacto_id", c.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ───────── Consultar estado de solicitud desde el conector ─────────
export const getSolicitudConectorEstado = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) =>
    z.object({
      demanda_id: z.string().uuid().nullable().optional(),
      anuncio_id: z.string().uuid(),
    }).parse(d),
  )
  .handler(async ({ data }) => {
    const userId = await resolveCurrentUserId();
    if (!userId) return { exists: false as const };
    const sb = getSupabaseAdmin();
    const { data: c } = await sb
      .from("contactos")
      .select("id")
      .eq("user_id", userId)
      .maybeSingle();
    if (!c) return { exists: false as const };
    // Una solicitud por persona y anuncio, venga o no de una demanda.
    const { data: existing } = await sb
      .from("solicitudes")
      .select("id, created_at, estado")
      .eq("contacto_id", c.id)
      .eq("anuncio_id", data.anuncio_id)
      .neq("estado", "cancelada")
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    return existing
      ? { exists: true as const, id: existing.id, created_at: existing.created_at as string }
      : { exists: false as const };
  });

// ───────── Cancelar solicitud desde el conector ─────────
export const cancelarSolicitudConector = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z.object({
      demanda_id: z.string().uuid().nullable().optional(),
      anuncio_id: z.string().uuid(),
    }).parse(d),
  )
  .handler(async ({ data }) => {
    const userId = await resolveCurrentUserId();
    if (!userId) throw new Error("Debes iniciar sesión");
    const sb = getSupabaseAdmin();
    const { data: c } = await sb
      .from("contactos")
      .select("id")
      .eq("user_id", userId)
      .maybeSingle();
    if (!c) throw new Error("Contacto no encontrado");
    const { error } = await sb
      .from("solicitudes")
      .update({ estado: "cancelada" })
      .eq("contacto_id", c.id)
      .eq("anuncio_id", data.anuncio_id)
      .neq("estado", "cancelada");
    if (error) throw new Error(error.message);
    return { ok: true };
  });


// ───────── Crear solicitud VIP (usuario logueado) ─────────
const SolicitudVipInput = z.object({
  descripcion: z.string().trim().min(10).max(4000),
});

export const createSolicitudVip = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => SolicitudVipInput.parse(d))
  .handler(async ({ data }) => {
    const sb = getSupabaseAdmin();
    const userId = await resolveCurrentUserId();
    if (!userId) throw new Error("Debes iniciar sesión para solicitar acceso VIP");

    // Identidad unificada: enlaza con la ficha existente (user_id → email → teléfono)
    const contacto = { id: await resolveContactoIdDeUsuario(sb, userId) };


    const { error: eS } = await sb.from("solicitudes").insert({
      contacto_id: contacto.id,
      anuncio_id: null,
      origen: "conector_vip",
      estado: "sin_atender",
      solicitud: `[VIP] ${data.descripcion}`,
    });
    if (eS) throw new Error(eS.message);

    return { ok: true };
  });

// ───────── Crear solicitud sobre un anuncio (usuario logueado, área VIP) ─────────
const SolicitudVipAnuncioInput = z.object({
  anuncio_id: z.string().uuid(),
  mensaje: z.string().trim().min(1).max(4000),
});

export const createSolicitudVipAnuncio = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => SolicitudVipAnuncioInput.parse(d))
  .handler(async ({ data }) => {
    const sb = getSupabaseAdmin();
    const { requireVipAccess } = await import("./vip-access.server");
    const userId = await requireVipAccess();
    if (!userId) throw new Error("Debes iniciar sesión");

    const contacto = { id: await resolveContactoIdDeUsuario(sb, userId) };


    // Evitar duplicados: si ya hay solicitud de este contacto para este anuncio, devolverla
    const { data: existente } = await sb
      .from("solicitudes")
      .select("id, created_at")
      .eq("contacto_id", contacto.id)
      .eq("anuncio_id", data.anuncio_id)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (existente) {
      return { ok: true, created_at: existente.created_at, already: true };
    }

    // Resolver agencia del anuncio
    const { data: anuncioRow, error: anuncioError } = await sb
      .from("anuncios")
      .select("agencia_id")
      .eq("id", data.anuncio_id)
      .maybeSingle();

    if (anuncioError || !anuncioRow) throw new Error("Anuncio no disponible.");

    const { data: row, error: eS } = await sb
      .from("solicitudes")
      .insert({
        contacto_id: contacto.id,
        anuncio_id: data.anuncio_id,
        agencia_id: anuncioRow?.agencia_id ?? null,
        origen: "conector_vip",
        estado: "sin_atender",
        solicitud: data.mensaje,
      })
      .select("id, created_at")
      .single();
    if (eS) throw new Error(eS.message);

    const { prepararNotificacionSolicitud } = await import("./solicitud-email.server");
    await prepararNotificacionSolicitud(sb, row.id);

    return { ok: true, created_at: row.created_at, already: false };
  });

export const getMiSolicitudAnuncio = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) =>
    z.object({ anuncio_id: z.string().uuid() }).parse(d),
  )
  .handler(async ({ data }) => {
    const sb = getSupabaseAdmin();
    const userId = await resolveCurrentUserId();
    if (!userId) return { exists: false as const };

    const { data: contacto } = await sb
      .from("contactos")
      .select("id")
      .eq("user_id", userId)
      .maybeSingle();
    if (!contacto) return { exists: false as const };

    const { data: row } = await sb
      .from("solicitudes")
      .select("id, created_at")
      .eq("contacto_id", contacto.id)
      .eq("anuncio_id", data.anuncio_id)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (!row) return { exists: false as const };
    return { exists: true as const, created_at: row.created_at };
  });

// ───────── IDs de anuncios en los que el usuario tiene solicitud activa ─────────
export const listMisSolicitudesAnuncioIds = createServerFn({ method: "GET" })
  .handler(async () => {
    const userId = await resolveCurrentUserId();
    if (!userId) return { ids: [] as string[] };
    const sb = getSupabaseAdmin();
    const { data: contacto } = await sb
      .from("contactos")
      .select("id")
      .eq("user_id", userId)
      .maybeSingle();
    if (!contacto) return { ids: [] as string[] };
    const { data: rows, error } = await sb
      .from("solicitudes")
      .select("anuncio_id")
      .eq("contacto_id", contacto.id)
      .neq("estado", "cancelada")
      .not("anuncio_id", "is", null);
    if (error) throw new Error(error.message);
    const ids = Array.from(
      new Set(
        (rows ?? [])
          .map((r: { anuncio_id: string | null }) => r.anuncio_id)
          .filter((v): v is string => !!v),
      ),
    );
    return { ids };
  });

// ───────── Opciones para selects ─────────
export const listSolicitudOpciones = createServerFn({ method: "GET" }).handler(
  async () => {
    const sb = getSupabaseAdmin();
    const scope = await getScope();

    // Contactos (solo staff puede ver el listado completo)
    let contactos: Array<{ id: string; nombre: string; apellidos: string | null; email: string | null }> = [];
    if (scope.isStaff) {
      const { data, error: e1 } = await sb
        .from("contactos")
        .select("id, nombre, apellidos, email")
        .order("nombre", { ascending: true })
        .limit(2000);
      if (e1) throw new Error(e1.message);
      contactos = (data ?? []) as typeof contactos;
    }

    // Anuncios (scoped)
    let aq = sb
      .from("anuncios")
      .select("id, referencia, alias, agencia_id")
      .order("created_at", { ascending: false })
      .limit(2000);
    if (!scope.isStaff) {
      if (scope.agenciaIds.length === 0)
        return { contactos, anuncios: [], asignables: [] };
      aq = aq.in("agencia_id", scope.agenciaIds);
    }
    const { data: anuncios, error: e2 } = await aq;
    if (e2) throw new Error(e2.message);

    // Asignables: staff + miembros de las agencias en scope
    let asignablesIds: string[] = [];
    const { data: staffRoles } = await sb
      .from("user_roles")
      .select("user_id")
      .in("role", ["admin", "customer_service", "agente"]);
    asignablesIds.push(
      ...(staffRoles ?? []).map((r: { user_id: string }) => r.user_id),
    );

    const agenciaScopeIds = scope.isStaff
      ? null
      : scope.agenciaIds;
    let miembrosQ = sb.from("agencia_miembros").select("user_id, agencia_id");
    if (agenciaScopeIds) miembrosQ = miembrosQ.in("agencia_id", agenciaScopeIds);
    const { data: miembros } = await miembrosQ;
    asignablesIds.push(
      ...(miembros ?? []).map((m: { user_id: string }) => m.user_id),
    );

    const uniq = Array.from(new Set(asignablesIds));
    // `staff` y `agencia_ids` permiten al formulario ofrecer solo a quien el
    // trigger `tg_datos_agencia_*` aceptará para la agencia de la solicitud.
    const staffSet = new Set((staffRoles ?? []).map((r: { user_id: string }) => r.user_id));
    const agenciasDe = new Map<string, string[]>();
    for (const m of (miembros ?? []) as Array<{ user_id: string; agencia_id: string }>) {
      agenciasDe.set(m.user_id, [...(agenciasDe.get(m.user_id) ?? []), m.agencia_id]);
    }
    let asignables: Array<{
      user_id: string;
      nombre: string;
      apellidos: string | null;
      email: string | null;
      staff: boolean;
      agencia_ids: string[];
    }> = [];
    if (uniq.length) {
      const { data: cs } = await sb
        .from("contactos")
        .select("user_id, nombre, apellidos, email")
        .in("user_id", uniq);
      asignables = (
        (cs ?? []) as Array<{
          user_id: string;
          nombre: string;
          apellidos: string | null;
          email: string | null;
        }>
      ).map((c) => ({
        ...c,
        staff: staffSet.has(c.user_id),
        agencia_ids: agenciasDe.get(c.user_id) ?? [],
      }));
    }

    return {
      contactos,
      anuncios: anuncios ?? [],
      asignables,
    };
  },
);
